import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, MinusCircle } from "lucide-react";

/**
 * Contexto editorial de las herramientas gratuitas (Fase 3).
 *
 * Motivo: las cuatro herramientas individuales eran páginas de ~200 palabras,
 * sin enlaces entrantes y sin explicar quién las publica, para quién sirven,
 * qué miden realmente y qué hacer con el resultado. Este bloque añade el
 * contexto mínimo para que la URL se entienda de forma aislada (WHO / WHAT /
 * WHO FOR / HOW / LIMITS / NEXT STEP) sin rellenar con texto genérico.
 *
 * El contenido es específico de cada herramienta: no se reutiliza el mismo
 * párrafo entre páginas.
 */

type Language = "es" | "en";

export type ToolKey =
  | "word-counter"
  | "url-analyzer"
  | "keyword-density"
  | "meta-tags-generator";

interface ToolCopy {
  answer: string;
  facts: string[];
  includes: string[];
  excludes: string[];
  interpretation: { q: string; a: string }[];
  related: { label: string; to: string }[];
}

const COPY: Record<ToolKey, Record<Language, ToolCopy>> = {
  "word-counter": {
    es: {
      answer:
        "Este contador mide extensión, densidad de párrafos y tiempo de lectura de un texto antes de publicarlo. No decide si el contenido posicionará: mide si está estructurado para leerse.",
      facts: [
        "Qué mide: palabras, caracteres, párrafos, oraciones y tiempo estimado de lectura",
        "Para quién: equipos de marketing y redactores que preparan contenido comercial",
        "Uso típico: revisar una página de servicio o un artículo antes de publicarlo",
        "Procesamiento: en el navegador; el texto no se envía a un servidor",
        "Publicada por: Luis ROC, consultor SEO — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Conteo exacto de palabras y caracteres con y sin espacios",
        "Promedio de longitud de oración y de párrafo",
        "Tiempo de lectura estimado para audiencia general",
      ],
      excludes: [
        "No evalúa intención de búsqueda ni competencia",
        "No comprueba originalidad ni plagio",
        "No sustituye una revisión editorial humana",
      ],
      interpretation: [
        {
          q: "¿Cuántas palabras necesita una página?",
          a: "Las que exijan la pregunta y la competencia de esa consulta. En páginas de servicio B2B revisadas en proyectos propios, el problema habitual no es la extensión, sino que falta la respuesta directa en los dos primeros párrafos.",
        },
        {
          q: "¿Un texto más largo posiciona mejor?",
          a: "No por sí solo. La extensión solo ayuda cuando añade información que la página no tenía: alcance, criterios, limitaciones, evidencia.",
        },
      ],
      related: [
        { label: "Densidad de palabras clave", to: "/herramientas/densidad-palabras" },
        { label: "Generador de meta tags", to: "/herramientas/generador-metatags" },
        { label: "Consultoría de SEO técnico", to: "/servicios/seo-tecnico" },
      ],
    },
    en: {
      answer:
        "This counter measures length, paragraph structure and reading time before you publish. It does not decide whether content will rank: it measures whether the text is structured to be read.",
      facts: [
        "Measures: words, characters, paragraphs, sentences and estimated reading time",
        "Built for: marketing teams and writers preparing commercial content",
        "Typical use: reviewing a service page or an article before publishing",
        "Processing: runs in the browser; text is not sent to a server",
        "Published by: Luis ROC, SEO consultant — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Exact word and character counts, with and without spaces",
        "Average sentence and paragraph length",
        "Estimated reading time for a general audience",
      ],
      excludes: [
        "Does not evaluate search intent or competition",
        "Does not check originality or plagiarism",
        "Does not replace human editorial review",
      ],
      interpretation: [
        {
          q: "How many words does a page need?",
          a: "As many as the question and the competing results require. On B2B service pages reviewed in my own projects, the usual problem is not length but a missing direct answer in the first two paragraphs.",
        },
        {
          q: "Does longer content rank better?",
          a: "Not on its own. Length helps only when it adds information the page did not have: scope, criteria, limitations, evidence.",
        },
      ],
      related: [
        { label: "Keyword density checker", to: "/en/tools/keyword-density" },
        { label: "Meta tags generator", to: "/en/tools/meta-tags-generator" },
        { label: "Technical SEO consulting", to: "/en/services/technical-seo" },
      ],
    },
  },
  "url-analyzer": {
    es: {
      answer:
        "Este analizador revisa la estructura de una URL: longitud, profundidad de carpetas, separadores, parámetros y legibilidad. Una URL bien formada no posiciona por sí sola, pero una mal formada complica el rastreo, el reporting y el enlazado.",
      facts: [
        "Qué revisa: longitud, niveles de carpeta, guiones, mayúsculas, parámetros y caracteres especiales",
        "Para quién: responsables de web y equipos que planifican una arquitectura o una migración",
        "Momento de uso: antes de publicar una sección nueva o de un rediseño",
        "Criterio: legibilidad para personas y estabilidad para buscadores",
        "Publicada por: Luis ROC — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Detección de parámetros de seguimiento que generan URLs duplicadas",
        "Aviso de profundidad excesiva de carpetas",
        "Revisión de separadores y caracteres poco recomendables",
      ],
      excludes: [
        "No comprueba el código de estado ni los redirects reales del servidor",
        "No decide si la URL debe cambiar: eso depende del histórico de indexación",
        "No sustituye un plan de redirecciones en una migración",
      ],
      interpretation: [
        {
          q: "¿Debo cambiar una URL que ya está indexada?",
          a: "Solo con una razón funcional, nunca por estética. En este mismo sitio, la decisión documentada fue conservar las URLs existentes y arreglar contenido y señales, cambiando únicamente una URL que servía contenido duplicado, con su 301 correspondiente.",
        },
        {
          q: "¿Los parámetros UTM en enlaces internos son un problema?",
          a: "Sí. Generan variantes de la misma página y reparten señales. En la revisión técnica de este sitio se retiraron de todos los enlaces internos.",
        },
      ],
      related: [
        { label: "Contador de palabras", to: "/herramientas/contador-palabras" },
        { label: "SEO técnico", to: "/servicios/seo-tecnico" },
        { label: "Metodología de trabajo", to: "/metodologia" },
      ],
    },
    en: {
      answer:
        "This analyzer reviews URL structure: length, folder depth, separators, parameters and readability. A clean URL does not rank on its own, but a malformed one complicates crawling, reporting and internal linking.",
      facts: [
        "Reviews: length, folder depth, hyphens, uppercase, parameters and special characters",
        "Built for: web owners and teams planning an architecture or a migration",
        "When to use it: before publishing a new section or a redesign",
        "Criteria: readable for people, stable for search engines",
        "Published by: Luis ROC — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Detection of tracking parameters that create duplicate URLs",
        "Warning on excessive folder depth",
        "Review of separators and discouraged characters",
      ],
      excludes: [
        "Does not check real server status codes or redirects",
        "Does not decide whether a URL should change — indexing history does",
        "Does not replace a redirect plan during a migration",
      ],
      interpretation: [
        {
          q: "Should I change a URL that is already indexed?",
          a: "Only for a functional reason, never for aesthetics. On this site the documented decision was to keep existing URLs and fix content and signals instead, changing only one URL that served duplicate content, with its 301 in place.",
        },
        {
          q: "Are UTM parameters in internal links a problem?",
          a: "Yes. They create variants of the same page and split signals. During this site's technical review they were removed from every internal link.",
        },
      ],
      related: [
        { label: "Word counter", to: "/en/tools/word-counter" },
        { label: "Technical SEO", to: "/en/services/technical-seo" },
        { label: "Working methodology", to: "/en/methodology" },
      ],
    },
  },
  "keyword-density": {
    es: {
      answer:
        "La densidad de palabras clave indica cuántas veces aparece un término respecto al total de palabras. Sirve para detectar repetición excesiva o ausencia del tema principal, no para alcanzar un porcentaje objetivo.",
      facts: [
        "Qué calcula: frecuencia y porcentaje de términos y combinaciones de dos y tres palabras",
        "Para quién: equipos que revisan textos comerciales ya escritos",
        "Uso correcto: diagnóstico de repetición, no objetivo de redacción",
        "Riesgo que detecta: sobreoptimización y canibalización entre páginas",
        "Publicada por: Luis ROC — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Ranking de términos más frecuentes del texto",
        "Porcentaje sobre el total de palabras",
        "Detección de combinaciones repetidas",
      ],
      excludes: [
        "No existe un porcentaje ideal universal",
        "No mide relevancia semántica ni cobertura del tema",
        "No compara el texto con los resultados que ya posicionan",
      ],
      interpretation: [
        {
          q: "¿Cuál es la densidad correcta?",
          a: "No hay una cifra oficial. Lo útil es la señal contraria: si un término aparece tantas veces que el texto suena forzado, el problema es de redacción antes que de SEO.",
        },
        {
          q: "¿Dos páginas pueden competir por el mismo término?",
          a: "Sí, y es un problema frecuente. En la auditoría de este sitio se detectaron títulos e intenciones solapadas entre páginas locales; la solución fue diferenciar la intención, no repetir más el término.",
        },
      ],
      related: [
        { label: "Contador de palabras", to: "/herramientas/contador-palabras" },
        { label: "Analizador de URLs", to: "/herramientas/analizador-url" },
        { label: "Consultoría SEO", to: "/servicios" },
      ],
    },
    en: {
      answer:
        "Keyword density shows how often a term appears relative to total word count. It is useful for spotting excessive repetition or a missing main topic — not for hitting a target percentage.",
      facts: [
        "Calculates: frequency and percentage of terms plus two- and three-word combinations",
        "Built for: teams reviewing commercial copy that is already written",
        "Correct use: repetition diagnosis, not a writing target",
        "Risk it surfaces: over-optimization and cannibalization between pages",
        "Published by: Luis ROC — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Ranking of the most frequent terms in the text",
        "Percentage over total word count",
        "Detection of repeated phrases",
      ],
      excludes: [
        "There is no universal ideal percentage",
        "Does not measure semantic relevance or topic coverage",
        "Does not compare the text against pages that already rank",
      ],
      interpretation: [
        {
          q: "What is the right density?",
          a: "There is no official figure. The useful signal is the opposite one: if a term appears so often that the text reads unnaturally, it is a writing problem before an SEO problem.",
        },
        {
          q: "Can two pages compete for the same term?",
          a: "Yes, and it happens often. This site's own audit found overlapping titles and intents across local pages; the fix was differentiating intent, not repeating the term more.",
        },
      ],
      related: [
        { label: "Word counter", to: "/en/tools/word-counter" },
        { label: "URL analyzer", to: "/en/tools/url-analyzer" },
        { label: "SEO consulting", to: "/en/services" },
      ],
    },
  },
  "meta-tags-generator": {
    es: {
      answer:
        "Este generador produce un borrador de title, meta description y etiquetas Open Graph con las longitudes habituales de Google y de las redes sociales. El texto final debe revisarse: Google reescribe títulos cuando no coinciden con el contenido de la página.",
      facts: [
        "Genera: title, meta description y etiquetas Open Graph básicas",
        "Longitudes de referencia: ~60 caracteres de título y ~155 de descripción",
        "Para quién: equipos que publican páginas nuevas sin soporte técnico permanente",
        "Regla aplicada: una sola etiqueta de cada tipo por página",
        "Publicada por: Luis ROC — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Borrador editable de título y descripción",
        "Bloque HTML listo para pegar en la cabecera",
        "Etiquetas sociales con el mismo mensaje que la página",
      ],
      excludes: [
        "No garantiza que Google muestre ese título",
        "No define la etiqueta canónica ni el hreflang",
        "No sustituye la revisión de duplicados en todo el sitio",
      ],
      interpretation: [
        {
          q: "¿Por qué Google muestra otro título?",
          a: "Porque considera que el suyo describe mejor el contenido de la página. Cuando eso pasa de forma sistemática, el problema suele estar en el contenido, no en la etiqueta.",
        },
        {
          q: "¿Puede una página tener dos meta descriptions?",
          a: "No debería. Es un fallo frecuente en sitios con plantilla más etiquetas dinámicas; en este sitio se detectó y se corrigió dejando una sola etiqueta por página.",
        },
      ],
      related: [
        { label: "Contador de palabras", to: "/herramientas/contador-palabras" },
        { label: "SEO técnico", to: "/servicios/seo-tecnico" },
        { label: "Search Library", to: "/biblioteca" },
      ],
    },
    en: {
      answer:
        "This generator drafts a title tag, meta description and Open Graph tags within the lengths Google and social platforms usually display. The output still needs review: Google rewrites titles when they do not match page content.",
      facts: [
        "Generates: title tag, meta description and basic Open Graph tags",
        "Reference lengths: ~60 characters for the title, ~155 for the description",
        "Built for: teams publishing new pages without permanent technical support",
        "Rule applied: one tag of each type per page",
        "Published by: Luis ROC — ROC Worldwide Agency LLC (Shenandoah, TX)",
      ],
      includes: [
        "Editable title and description draft",
        "Ready-to-paste HTML head block",
        "Social tags carrying the same message as the page",
      ],
      excludes: [
        "Does not guarantee Google will display that title",
        "Does not define the canonical tag or hreflang",
        "Does not replace a site-wide duplicate check",
      ],
      interpretation: [
        {
          q: "Why does Google show a different title?",
          a: "Because it considers its own version a better description of the page. When that happens systematically, the problem is usually the content, not the tag.",
        },
        {
          q: "Can a page have two meta descriptions?",
          a: "It should not. It is a common failure on sites that combine a template with dynamic tags; this site had it and it was fixed by keeping a single tag per page.",
        },
      ],
      related: [
        { label: "Word counter", to: "/en/tools/word-counter" },
        { label: "Technical SEO", to: "/en/services/technical-seo" },
        { label: "Search Library", to: "/en/library" },
      ],
    },
  },
};

const LABELS = {
  es: {
    summary: "En resumen",
    includes: "Qué hace",
    excludes: "Qué no hace",
    questions: "Preguntas frecuentes sobre esta herramienta",
    related: "Recursos relacionados",
    ctaTitle: "¿Necesitas revisar un sitio completo, no solo un texto?",
    ctaBody:
      "Las herramientas dan una señal puntual. Un diagnóstico revisa rastreo, indexación, arquitectura, contenido y conversión en conjunto.",
    ctaButton: "Reunión inicial gratuita de 30 minutos",
    ctaLink: "/contacto",
    hub: "Todas las herramientas",
    hubLink: "/herramientas-seo",
  },
  en: {
    summary: "In short",
    includes: "What it does",
    excludes: "What it does not do",
    questions: "Questions about this tool",
    related: "Related resources",
    ctaTitle: "Need a full site reviewed, not just a piece of text?",
    ctaBody:
      "Tools give a single signal. A review looks at crawling, indexing, architecture, content and conversion together.",
    ctaButton: "Free 30-minute initial consultation",
    ctaLink: "/en/contact",
    hub: "All tools",
    hubLink: "/en/seo-tools",
  },
} as const;

interface ToolContextProps {
  tool: ToolKey;
  language: Language;
}

export const ToolContext = ({ tool, language }: ToolContextProps) => {
  const c = COPY[tool][language];
  const l = LABELS[language];

  return (
    <section className="mt-16 space-y-10" aria-label={l.summary}>
      <div>
        <p className="text-lg leading-relaxed text-foreground">{c.answer}</p>
      </div>

      <Card className="bg-muted/40">
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">{l.summary}</h2>
          <ul className="space-y-2 text-muted-foreground">
            {c.facts.map((f) => (
              <li key={f} className="flex gap-2">
                <span className="text-primary">•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h2 className="text-xl font-semibold mb-3">{l.includes}</h2>
          <ul className="space-y-2 text-muted-foreground">
            {c.includes.map((i) => (
              <li key={i} className="flex gap-2">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-xl font-semibold mb-3">{l.excludes}</h2>
          <ul className="space-y-2 text-muted-foreground">
            {c.excludes.map((i) => (
              <li key={i} className="flex gap-2">
                <MinusCircle className="h-5 w-5 shrink-0 text-muted-foreground" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">{l.questions}</h2>
        <div className="space-y-5">
          {c.interpretation.map((qa) => (
            <div key={qa.q}>
              <h3 className="font-medium mb-1">{qa.q}</h3>
              <p className="text-muted-foreground">{qa.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-3">{l.related}</h2>
        <ul className="flex flex-wrap gap-3">
          {c.related.map((r) => (
            <li key={r.to}>
              <Link
                to={r.to}
                className="text-primary underline underline-offset-4 hover:no-underline"
              >
                {r.label}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to={l.hubLink}
              className="text-muted-foreground underline underline-offset-4 hover:no-underline"
            >
              {l.hub}
            </Link>
          </li>
        </ul>
      </div>

      <Card className="border-primary/30">
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-2">{l.ctaTitle}</h2>
          <p className="text-muted-foreground mb-4">{l.ctaBody}</p>
          <Button asChild>
            <Link to={l.ctaLink}>
              {l.ctaButton}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </section>
  );
};
